import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, PolylineF, CircleF, OverlayViewF } from '@react-google-maps/api';
import { Search, ChevronDown, ChevronUp, ArrowLeft, Ship } from 'lucide-react';
import { useFindPathViaPoints, useSearchServices } from '@/core/api/queries';

const containerStyle = {
    width: '100%',
    height: '100vh'
};

const center = {
    lat: 54.5259,
    lng: 18.5212
};

// Helper function to extract port codes from rotation
const getRotationPortCodes = (rotation) => {
    if (!rotation?.ports || rotation.ports.length < 2) return null;

    const codes = rotation.ports
        .map(port => port.codes?.[0]) // Take first code only
        .filter(code => code); // Remove null/undefined

    if (codes.length < 2) return null;

    return {
        startCode: codes[0],
        endCode: codes[codes.length - 1],
        viaPoints: codes.slice(1, -1)
    };
};

// Colors for different rotations
const rotationColors = [
    '#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'
];

// Separate component to handle individual rotation path
const RotationPath = ({ codes, color, onPathLoad, rotation }) => {
    const { data: pathData, isLoading } = useFindPathViaPoints(
        codes.startCode,
        codes.endCode,
        codes.viaPoints,
        true
    );

    useEffect(() => {
        if (pathData?.nodes) {
            onPathLoad?.(pathData.nodes);
        }
    }, [pathData, onPathLoad]);

    if (isLoading || !pathData?.routes) return null;

    // Create a map of port codes to port names from rotation
    const portCodeToName = {};
    rotation?.ports?.forEach(port => {
        port.codes?.forEach(code => {
            portCodeToName[code] = port.name;
        });
    });

    return (
        <>
            {/* Render polylines */}
            {pathData.routes.map((route, routeIndex) => (
                <PolylineF
                    key={`route-${routeIndex}`}
                    path={route.path}
                    options={{
                        strokeColor: color,
                        strokeOpacity: 0.7,
                        strokeWeight: 3,
                        geodesic: true
                    }}
                />
            ))}

            {/* Render port markers */}
            {pathData.nodes?.map((node, nodeIndex) => {
                const portName = portCodeToName[node.code] || node.port_name || node.city;
                const isRotationPort = !!portCodeToName[node.code];

                return (
                    <React.Fragment key={`node-${nodeIndex}`}>
                        <CircleF
                            center={{ lat: node.lat, lng: node.lng }}
                            radius={500}
                            options={{
                                fillColor: color,
                                fillOpacity: 0.8,
                                strokeColor: '#ffffff',
                                strokeWeight: 2,
                                strokeOpacity: 1,
                            }}
                        />
                        {/* Show labels for all rotation ports */}
                        {isRotationPort && portName && (
                            <OverlayViewF
                                position={{ lat: node.lat, lng: node.lng }}
                                mapPaneName="overlayMouseTarget"
                            >
                                <div
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#333',
                                        border: `1px solid ${color}`,
                                        whiteSpace: 'nowrap',
                                        transform: 'translate(-50%, -150%)',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    {portName}
                                </div>
                            </OverlayViewF>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
};

const MapWithServiceSearch = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [advancedFilters, setAdvancedFilters] = useState({
        portName: '',
        portCode: '',
        operator: '',
        serviceName: '',
        serviceCode: ''
    });
    const [selectedService, setSelectedService] = useState(null);
    const [map, setMap] = useState(null);
    const [loadedPathNodes, setLoadedPathNodes] = useState([]);
    const [hasZoomed, setHasZoomed] = useState(false);
    const [selectedRotationIndex, setSelectedRotationIndex] = useState(null);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: 'AIzaSyBUfoGJxQTuhIXyfc3k2a9iRf32r8hr8Ug'
    });

    const { data, isLoading, error } = useSearchServices(
        {
            query: debouncedQuery,
            ...advancedFilters,
            limit: 50,
            offset: 0
        },
        true
    );

    const services = data?.hits || [];

    // Extract rotation paths when service is selected
    const rotationPaths = useMemo(() => {
        if (!selectedService?.rotations) return [];

        return selectedService.rotations
            .map((rotation, index) => ({
                rotation,
                codes: getRotationPortCodes(rotation),
                color: rotationColors[index % rotationColors.length],
                index
            }))
            .filter(item => item.codes !== null);
    }, [selectedService]);

    // Filter to show only selected rotation if one is selected
    const displayedRotationPaths = useMemo(() => {
        if (selectedRotationIndex === null) return rotationPaths;
        return rotationPaths.filter(rp => rp.index === selectedRotationIndex);
    }, [rotationPaths, selectedRotationIndex]);

    // Auto-zoom map to fit all rotation paths (only once per service)
    useEffect(() => {
        if (!map || loadedPathNodes.length === 0 || hasZoomed) return;

        const bounds = new window.google.maps.LatLngBounds();

        loadedPathNodes.forEach(node => {
            bounds.extend({ lat: node.lat, lng: node.lng });
        });

        // Add padding to account for right panel
        const padding = { top: 50, right: 450, bottom: 50, left: 50 };
        map.fitBounds(bounds, padding);

        setHasZoomed(true);
    }, [map, loadedPathNodes, hasZoomed]);

    // Reset zoom tracking when service changes
    useEffect(() => {
        setLoadedPathNodes([]);
        setHasZoomed(false);
        setSelectedRotationIndex(null);
    }, [selectedService]);

    const handlePathLoad = useCallback((nodes) => {
        setLoadedPathNodes(prev => [...prev, ...nodes]);
    }, []);

    const handleRotationClick = (rotationIndex) => {
        if (selectedRotationIndex === rotationIndex) {
            // Deselect if clicking the same rotation
            setSelectedRotationIndex(null);
        } else {
            // Select new rotation
            setSelectedRotationIndex(rotationIndex);
        }
        // Reset zoom to trigger new auto-zoom
        setLoadedPathNodes([]);
        setHasZoomed(false);
    };

    const getUniquePortCount = (rotations) => {
        const portNames = new Set();
        rotations?.forEach(r => r.ports?.forEach(p => portNames.add(p.name)));
        return portNames.size;
    };

    const handleFilterChange = (field, value) => {
        setAdvancedFilters(prev => ({ ...prev, [field]: value }));
    };

    const clearFilters = () => {
        setSearchQuery('');
        setAdvancedFilters({
            portName: '',
            portCode: '',
            operator: '',
            serviceName: '',
            serviceCode: ''
        });
    };

    if (!isLoaded) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={10}
                onLoad={setMap}
            >
                {/* Render paths for all rotations */}
                {displayedRotationPaths.map(({ codes, color, index, rotation }) => (
                    <RotationPath
                        key={`rotation-${index}`}
                        codes={codes}
                        color={color}
                        onPathLoad={handlePathLoad}
                        rotation={rotation}
                    />
                ))}
            </GoogleMap>

            {/* Side Panel */}
            <div className="absolute top-0 right-0 w-96 h-full bg-white shadow-xl flex flex-col">
                {/* Header */}
                <div className="p-4 border-b bg-gray-50">
                    {selectedService ? (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setSelectedService(null)}
                                className="p-1 hover:bg-gray-200 rounded"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <h2 className="text-xl font-bold text-gray-800">Service Details</h2>
                        </div>
                    ) : (
                        <h2 className="text-xl font-bold text-gray-800">Services</h2>
                    )}
                </div>

                {!selectedService ? (
                    <>
                        {/* Search Section */}
                        <div className="p-4 border-b space-y-3">
                            {/* Main Search */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search services..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Advanced Filters Toggle */}
                            <button
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                className="flex items-center justify-between w-full text-sm text-blue-600 hover:text-blue-700"
                            >
                                <span>Advanced Filters</span>
                                {showAdvancedFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>

                            {/* Advanced Filters */}
                            {showAdvancedFilters && (
                                <div className="space-y-2 pt-2">
                                    <input
                                        type="text"
                                        placeholder="Port Name"
                                        value={advancedFilters.portName}
                                        onChange={(e) => handleFilterChange('portName', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Port Code"
                                        value={advancedFilters.portCode}
                                        onChange={(e) => handleFilterChange('portCode', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Operator"
                                        value={advancedFilters.operator}
                                        onChange={(e) => handleFilterChange('operator', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Service Name"
                                        value={advancedFilters.serviceName}
                                        onChange={(e) => handleFilterChange('serviceName', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Service Code"
                                        value={advancedFilters.serviceCode}
                                        onChange={(e) => handleFilterChange('serviceCode', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <button
                                        onClick={clearFilters}
                                        className="text-sm text-gray-600 hover:text-gray-800 underline"
                                    >
                                        Clear all filters
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Services List */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {isLoading && (
                                <div className="text-center text-gray-500 py-8">Loading...</div>
                            )}
                            {error && (
                                <div className="text-center text-red-500 py-8">Error loading services</div>
                            )}
                            {!isLoading && !error && services.length === 0 && (
                                <div className="text-center text-gray-500 py-8">No services found</div>
                            )}
                            {!isLoading && !error && services.length > 0 && (
                                <div className="space-y-3">
                                    {services.map((service) => (
                                        <div
                                            key={service.id}
                                            onClick={() => setSelectedService(service)}
                                            className="p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all"
                                        >
                                            <div className="space-y-1">
                                                {/* Service Name */}
                                                <h3 className="font-semibold text-gray-800 truncate">
                                                    {service.serviceNames?.[0] || 'Unnamed Service'}
                                                </h3>
                                                {/* Service Codes */}
                                                <p className="text-sm text-gray-600 truncate">
                                                    {service.serviceCodes?.join(', ') || 'No codes'}
                                                </p>
                                                {/* Stats */}
                                                <p className="text-xs text-gray-500">
                                                    {service.rotations?.length || 0} rotations • {getUniquePortCount(service.rotations)} ports
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    /* Service Detail View */
                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="space-y-4">
                            {/* Service Names */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-1">Service Names</h3>
                                <div className="flex flex-wrap gap-1">
                                    {selectedService.serviceNames?.map((name, i) => (
                                        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                                            {name}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Service Codes */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-1">Service Codes</h3>
                                <div className="flex flex-wrap gap-1">
                                    {selectedService.serviceCodes?.map((code, i) => (
                                        <span key={i} className="px-2 py-1 bg-gray-100 text-gray-800 text-sm rounded">
                                            {code}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Operators */}
                            {selectedService.operators && selectedService.operators.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-1">Operators</h3>
                                    <div className="flex flex-wrap gap-1">
                                        {selectedService.operators.map((operator, i) => (
                                            <span key={i} className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded">
                                                {operator}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Rotations */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase">Rotations</h3>
                                    {selectedRotationIndex !== null && (
                                        <button
                                            onClick={() => handleRotationClick(null)}
                                            className="text-xs text-blue-600 hover:text-blue-700 underline"
                                        >
                                            Show All
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    {rotationPaths.map(({ rotation, color, index }) => (
                                        <div
                                            key={index}
                                            onClick={() => handleRotationClick(index)}
                                            className={`border rounded-lg p-3 cursor-pointer transition-all ${selectedRotationIndex === index
                                                ? 'border-2 shadow-md'
                                                : 'border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                                                }`}
                                            style={selectedRotationIndex === index ? { borderColor: color } : {}}
                                        >
                                            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                                <div
                                                    className="w-4 h-4 rounded-full"
                                                    style={{ backgroundColor: color }}
                                                />
                                                <Ship size={16} />
                                                Rotation {rotation.name}
                                            </h4>
                                            <div className="space-y-2">
                                                {rotation.ports?.map((port, j) => (
                                                    <div key={j} className="flex justify-between items-center text-sm">
                                                        <span className="text-gray-700">{port.name}</span>
                                                        {port.day !== null && port.day !== undefined && (
                                                            <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                                                Day {port.day}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Source URL */}
                            {selectedService.sourceUrl && (
                                <div>
                                    <a
                                        href={selectedService.sourceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 hover:underline"
                                    >
                                        View source →
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MapWithServiceSearch;